import classnames from 'classnames';
import { memo } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  IconButton,
  DropdownInput,
  CheckboxInput,
  ArrowLeft as leftIcon,
  ArrowRight as rightIcon,
  IcNoDataEvent as noDataEventIcon,
  CalendarIcon as calendarIcon,
} from '@/shared/ui';
import useAfibHrChartOptions from './useAfibHrChartOptions';

const ENUM_EVENT_ACTION = {
  OTHER_EVENT: 'other_event',
  ARTIFACT_EVENT: 'artifact_event',
  AFIB_EVENT: 'afib_event',
};

export type AfibHrChartOptionsProps = {
  timezoneOffset: number;
  studyId: string;
  profileId: string;
  startReport: any;
  stopReport: any;
};

/**
 * Navigation bar under the AFib heart rate chart for moving between recorded days. Renders a skeleton while the chart or its options load, then previous and next day arrows bounded by the daily count, a date dropdown, and the AFib, artifact and no-data legend entries, which are hidden when the day holds no data.
 *
 * @param props - The options props, carrying profileId, studyId, timezoneOffset and the report start and stop times.
 * @returns The rendered date navigator with its legend, or its skeleton while data loads.
 */
const AfibHrChartOptions = ({
  timezoneOffset,
  studyId,
  profileId,
  startReport,
  stopReport,
}: AfibHrChartOptionsProps) => {
  const {
    isLoadingHrChartOptions,
    isLoadingHrChart,
    dateIndex,
    dailyCount,
    isEmptyData,
    isAfibEvent,
    dropdownOptions,
    formatSelectedDate,
    onChangeDate,
    handleClickArrow,
    toggleDisplayRhythmEvent,
  } = useAfibHrChartOptions({ timezoneOffset, studyId, profileId, startReport, stopReport });

  if (isLoadingHrChartOptions || isLoadingHrChart) {
    return (
      <div className="navigator-skeleton-container">
        <div className="skeleton-container">
          <Skeleton height="100%" />
        </div>
      </div>
    );
  }

  return (
    <div id="hr-chart-options" className="ecg-options-event">
      <div className="w-33" />
      <div className="select-date w-33">
        <IconButton
          className="me-2"
          isFilled
          disabled={dateIndex === 0}
          iconComponent={<img src={leftIcon} alt="Left icon" />}
          onClick={() => {
            handleClickArrow(false);
          }}
        />

        <DropdownInput
          name="date"
          disabled={!dailyCount?.length}
          options={dropdownOptions}
          value={formatSelectedDate}
          onChange={onChangeDate}
          selectedItemIcon={<img src={calendarIcon} alt="Icon calendar" />}
          showCaret={false}
          direction="up"
          container="body"
        />

        <IconButton
          className="ms-2"
          isFilled
          disabled={dateIndex === (dailyCount?.length ?? 0) - 1}
          iconComponent={<img src={rightIcon} alt="Right icon" />}
          onClick={() => {
            handleClickArrow(true);
          }}
        />
      </div>
      {isEmptyData ? (
        <div className="w-33" />
      ) : (
        <div className="event-info-container w-33">
          {isAfibEvent ? (
            <>
              <CheckboxInput
                className={classnames('event-info-checkbox', '--afib', 'always-visible')}
                id="afibEvent"
                name="afibEvent"
                data={
                  {
                    label: 'AFib',
                    value: false,
                  } as any
                }
                checked={false}
                onChange={() => {}}
              />
              {/* <CheckboxInput
                    className={classnames('event-info-checkbox', '--other')}
                    id="otherEvent"
                    name="otherEvent"
                    data={{
                      label: 'Pause & VE events',
                      value: isShowOtherEvent,
                    }}
                    checked={isShowOtherEvent}
                    onChange={() => { toggleDisplayRhythmEvent(ENUM_EVENT_ACTION.OTHER_EVENT); }}
                  /> */}
            </>
          ) : (
            <>
              {/* <CheckboxInput
                    className={classnames('event-info-checkbox', '--other', 'always-visible')}
                    id="otherEvent"
                    name="otherEvent"
                    data={{
                      label: eventTypeConvert[activeButton],
                      value: false,
                    }}
                    checked={false}
                    onChange={() => { }}
                  /> */}
              <CheckboxInput
                className={classnames('event-info-checkbox', '--afib', 'always-visible')}
                id="afibEvent"
                name="afibEvent"
                data={
                  {
                    label: 'AFib',
                    value: false,
                  } as any
                }
                checked={false}
                onChange={() => {
                  toggleDisplayRhythmEvent(ENUM_EVENT_ACTION.AFIB_EVENT);
                }}
              />
            </>
          )}
          <CheckboxInput
            className={classnames('event-info-checkbox', '--artifact', 'always-visible')}
            id="artifact"
            name="artifact"
            data={
              {
                label: 'Artifact',
                value: false,
              } as any
            }
            checked={false}
            onChange={() => toggleDisplayRhythmEvent(ENUM_EVENT_ACTION.ARTIFACT_EVENT)}
          />
          <div className={classnames('event-info-checkbox', '--no-data')}>
            <img src={noDataEventIcon} alt="no-data-event" />
            <div>No data</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(AfibHrChartOptions);
